import React from "react";
import { motion } from "framer-motion";
import { Droplets, Leaf, ShieldCheck, TrendingUp, ArrowRight, Sparkles } from "lucide-react";
import { AnimatedLines } from "@/components/landing/AnimatedLines";

const IMAGE =
  "https://images.unsplash.com/photo-1748063578185-3d68121b11ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const FEATURES = [
  {
    icon: Droplets,
    title: "Nettoyage vapeur doux",
    desc: "Haute température, sans produits agressifs : on respecte les finitions et surfaces encore neuves.",
  },
  {
    icon: Leaf,
    title: "Approche écologique",
    desc: "Moins de chimie, plus de vapeur — sain pour votre famille comme pour l'environnement.",
  },
  {
    icon: ShieldCheck,
    title: "Matériaux préservés",
    desc: "Façades, terrasses, menuiseries et joints protégés de l'usure et des salissures prématurées.",
  },
  {
    icon: TrendingUp,
    title: "Longévité & valeur",
    desc: "Un habitat entretenu dès le départ vieillit mieux et conserve durablement sa valeur.",
  },
];

const BLOCK_HIDDEN = { opacity: 0, y: 24 };
const BLOCK_SHOW = { opacity: 1, y: 0 };
const BLOCK_VIEWPORT = { once: true, margin: "-80px" };
const ITEM_HIDDEN = { opacity: 0, y: 20 };
const ITEM_VIEWPORT = { once: true };
const IMG_HIDDEN = { opacity: 0, scale: 0.96 };
const IMG_SHOW = { opacity: 1, scale: 1 };

export const PreventiveCare = ({ onReserve }) => {
  return (
    <section
      id="preventif"
      data-testid="preventive-section"
      className="relative overflow-hidden py-20 md:py-28 bg-background scroll-mt-44"
    >
      <AnimatedLines />
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={BLOCK_HIDDEN}
            whileInView={BLOCK_SHOW}
            viewport={BLOCK_VIEWPORT}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              <Sparkles className="w-4 h-4" /> Immobilier neuf · Entretien préventif
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink mt-4 leading-[1.05]">
              Préservez votre bien neuf, dès le premier jour
            </h2>
            <p className="text-muted-foreground mt-5 text-base sm:text-lg leading-relaxed">
              Pour une maison ou un appartement récent, mieux vaut prévenir que restaurer.
              Notre <span className="text-ink font-semibold">nettoyage doux à la vapeur</span> élimine
              salissures et dépôts sans jamais agresser vos surfaces neuves — une tranquillité
              d'esprit totale pour les propriétaires soucieux de leur investissement.
            </p>

            <div className="grid sm:grid-cols-2 gap-5 mt-9">
              {FEATURES.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={ITEM_HIDDEN}
                  whileInView={BLOCK_SHOW}
                  viewport={ITEM_VIEWPORT}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex gap-4"
                >
                  <span className="w-11 h-11 grid place-items-center bg-marble-light border border-border rounded-sm shrink-0">
                    <f.icon className="w-5 h-5 text-ink" />
                  </span>
                  <div>
                    <h3 className="font-heading font-bold text-base text-ink">{f.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{f.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <button
              data-testid="preventive-cta"
              onClick={() => onReserve("preventif")}
              className="cta mt-10 inline-flex items-center gap-2 bg-ink text-white font-bold px-7 py-4 rounded-sm hover:bg-ink-light"
            >
              Protéger mon bien neuf
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>

          <motion.div
            initial={IMG_HIDDEN}
            whileInView={IMG_SHOW}
            viewport={BLOCK_VIEWPORT}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-sm">
              <img
                src={IMAGE}
                alt="Maison neuve entretenue"
                className="w-full h-[420px] lg:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent" />
            </div>
            <div
              data-testid="preventive-badge"
              className="absolute bottom-6 left-6 right-6 sm:right-auto bg-white border border-border rounded-sm p-5 shadow-lg"
            >
              <div className="flex items-center gap-2 text-ink font-heading font-bold">
                <Droplets className="w-5 h-5" /> Programme dès la livraison
              </div>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-xs">
                Un planning d'entretien adapté à votre habitat neuf, pour qu'il reste
                impeccable saison après saison.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
